import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ensureStyles } from "./styles";
import { useSupportChat } from "./useSupportChat";
import { Markdown } from "./Markdown";
import { ATTACHMENT_ACCEPT } from "./attachments";
import type {
  SupportAttachment,
  SupportChatProps,
  SupportMessage,
} from "./types";

const DEFAULT_POLL_MS = 3000;

/**
 * True while a blocking dialog is present anywhere in the document.
 *
 * Detects `[role="dialog"][aria-modal="true"]` — what CoreUI's CModal
 * renders and what a hand-rolled overlay should set anyway — so one rule
 * covers both host apps without the widget knowing either app's markup.
 *
 * A MutationObserver rather than a poll: modals open on user action, and a
 * timer either lags visibly or burns wakeups on an idle page.
 */
function useModalPresent(): boolean {
  const [present, setPresent] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const check = () =>
      setPresent(
        document.querySelector('[role="dialog"][aria-modal="true"]') !== null,
      );

    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", "aria-modal"],
    });
    return () => observer.disconnect();
  }, []);

  return present;
}

/**
 * Floating support chat.
 *
 * Deliberately dependency-free: plain React plus a scoped stylesheet, no
 * Tailwind classes and no CoreUI components, so the same build renders
 * identically in tenant-frontend (Tailwind v4 / React 19) and
 * landlord-frontend (CoreUI + Bootstrap / React 18).
 *
 * The host injects a `transport` — the two apps authenticate differently and
 * neither should have to adopt the other's HTTP setup.
 */
export const SupportChat: React.FC<SupportChatProps> = ({
  transport,
  // Panel header. Full brand name here, where there is room for it.
  title = "Lighthouse Support",
  // The closed launcher is a small floating pill — the full name makes it
  // noticeably wider for no gain, so it stays short unless a host overrides
  // it. Falls back to `title` if only that is provided.
  launcherLabel,
  // Deliberately open-ended. Listing features ("ask about invitations,
  // tenants, renewals…") reads as a menu and quietly implies anything absent
  // from the list is out of scope — which then discourages the questions the
  // knowledge base can actually answer.
  greeting = "Tell us what you need help with and we'll take it from there.",
  pollIntervalMs = DEFAULT_POLL_MS,
  position = "bottom-right",
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const modalOpen = useModalPresent();
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    messages,
    awaitingReply,
    sending,
    error,
    pending,
    attachFiles,
    removeAttachment,
    send,
    dismissError,
  } = useSupportChat({ transport, active: open, pollIntervalMs });

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ensureStyles();
  }, []);

  // Pin to the newest message. Layout effect so it happens before paint and
  // the user never sees the transcript jump.
  useLayoutEffect(() => {
    if (!open) return;
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages, awaitingReply]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Escape closes the panel — expected of any overlay.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  /**
   * Fetch a fresh presigned URL and open it in a new tab.
   *
   * `window.open` is called synchronously with a placeholder and redirected
   * once the URL resolves — opening it inside the async callback instead
   * would be blocked as a popup, since the click gesture has expired by then.
   */
  const openAttachment = async (uploadUuid: string) => {
    const tab = window.open("", "_blank");
    try {
      const url = await transport.getDownloadUrl(uploadUuid);
      if (!url) throw new Error("no url");
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch {
      tab?.close();
    }
  };

  const uploading = pending.some((p) => p.uploading);
  const readyCount = pending.filter((p) => p.uploadUuid && !p.error).length;

  const submit = async () => {
    const body = draft.trim();
    // A file with no text is a valid message; an upload still in flight is not.
    if ((!body && readyCount === 0) || sending || uploading) return;
    setDraft("");
    await send(body);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter makes a newline — the convention everywhere.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  // `--open` only while the panel is showing. On mobile the open state
  // stretches the container edge-to-edge for a full-width panel; the closed
  // launcher must NOT stretch, or its invisible container covers whatever
  // sits beside it (e.g. a form's Continue button).
  const rootClass =
    `lh-support lh-support--${position}` + (open ? " lh-support--open" : "");

  // A blocking dialog is up: stay out of its way. The launcher's z-index
  // sits above any app's modal (deliberately, so it clears normal page
  // chrome), which means it otherwise floats over the dialog and covers
  // whatever sits in that corner — on the SDA signature modal, the Confirm
  // button. Hidden only while CLOSED: an already-open chat keeps rendering,
  // so a modal appearing underneath can't yank the panel away mid-sentence.
  if (!open && modalOpen) return null;

  if (!open) {
    return (
      <div className={rootClass}>
        <button
          type="button"
          className="lh-support__launcher"
          onClick={() => setOpen(true)}
          aria-label="Open support chat"
        >
          <ChatIcon />
          <span>{launcherLabel ?? "Support"}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <div
        className="lh-support__panel"
        role="dialog"
        aria-label="Support chat"
        aria-modal="false"
      >
        <div className="lh-support__header">
          <h2 className="lh-support__title">{title}</h2>
          <button
            type="button"
            className="lh-support__close"
            onClick={() => setOpen(false)}
            aria-label="Close support chat"
          >
            ×
          </button>
        </div>

        <div
          className="lh-support__body"
          ref={bodyRef}
          // Announce new messages to screen readers without stealing focus.
          aria-live="polite"
          aria-atomic="false"
        >
          {messages.length === 0 && (
            <p className="lh-support__greeting">{greeting}</p>
          )}
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              onOpenAttachment={openAttachment}
            />
          ))}
          {awaitingReply && (
            <div className="lh-support__typing" aria-label="Assistant is typing">
              <span />
              <span />
              <span />
            </div>
          )}
        </div>

        {error && (
          <div className="lh-support__error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={dismissError} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}

        <div className="lh-support__footer">
          {pending.length > 0 && (
            <div className="lh-support__pending">
              {pending.map((p) => (
                <span
                  key={p.key}
                  className={`lh-support__chip${p.error ? " lh-support__chip--error" : ""}`}
                >
                  <span className="lh-support__chip-name">{p.fileName}</span>
                  {p.uploading ? (
                    <span className="lh-support__chip-status">uploading…</span>
                  ) : p.error ? (
                    <span className="lh-support__chip-status">{p.error}</span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => removeAttachment(p.key)}
                    aria-label={`Remove ${p.fileName}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="lh-support__composer">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.length) void attachFiles(e.target.files);
                // Reset so selecting the same file twice still fires onChange.
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="lh-support__attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              aria-label="Attach a file"
              title="Attach a file"
            >
              <PaperclipIcon />
            </button>
            <textarea
              ref={inputRef}
              className="lh-support__input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type your message…"
              rows={1}
              disabled={sending}
              aria-label="Message"
            />
            <button
              type="button"
              className="lh-support__send"
              onClick={() => void submit()}
              disabled={
                sending ||
                uploading ||
                (draft.trim().length === 0 && readyCount === 0)
              }
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Launcher icon.
 *
 * An inline SVG rather than the 💬 emoji: emoji are rendered by the OS font,
 * so they arrive full-colour and differently shaped on every platform, which
 * fought the brand palette. This inherits `currentColor`, so it always
 * matches the button's text and needs no separate colour rule.
 *
 * `aria-hidden` because the adjacent label already names the control, and the
 * button carries its own aria-label.
 */
const ChatIcon: React.FC = () => (
  <svg
    className="lh-support__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

/** Same line-icon treatment as the launcher; inherits `currentColor`. */
const PaperclipIcon: React.FC = () => (
  <svg
    className="lh-support__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

/**
 * Attachment link inside a message bubble.
 *
 * The presigned URL is fetched on click rather than up front: it is
 * short-lived (300s), so binding it at render would leave dead links in a
 * transcript the user scrolls back to later.
 */
const AttachmentLink: React.FC<{
  attachment: SupportAttachment;
  onOpen: (uploadUuid: string) => void;
}> = ({ attachment, onOpen }) => (
  <button
    type="button"
    className="lh-support__attachment"
    onClick={() => onOpen(attachment.uploadUuid)}
  >
    <PaperclipIcon />
    <span>{attachment.fileName}</span>
  </button>
);

const MessageBubble: React.FC<{
  message: SupportMessage;
  onOpenAttachment: (uploadUuid: string) => void;
}> = ({ message, onOpenAttachment }) => {
  if (message.role === "system") {
    return <div className="lh-support__msg lh-support__msg--system">{message.body}</div>;
  }
  return (
    <div className={`lh-support__msg lh-support__msg--${message.role}`}>
      {message.role === "admin" && (
        <span className="lh-support__author">
          {message.adminName ?? "Support"}
        </span>
      )}
      {/* The assistant writes markdown (the knowledge base is written in it),
          so `**Send Verification Code**` has to render as bold rather than as
          literal asterisks. Rendered to React elements, never HTML — see
          Markdown.tsx. */}
      {message.body ? <Markdown body={message.body} /> : null}
      {message.attachments?.length ? (
        <div className="lh-support__attachments">
          {message.attachments.map((a) => (
            <AttachmentLink
              key={a.uuid}
              attachment={a}
              onOpen={onOpenAttachment}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SupportChat;
